import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthError, requestPasswordReset } from './auth';
import { AuthField, AuthLink, AuthNotice, AuthShell, AuthSubmit } from './AuthShell';

export default function ForgotPassword({ onBack }: { onBack: () => void }): ReactElement {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : t('com_ui_leadhr_login_generic_error'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * The confirmation never says whether the address is registered. The server is
   * deliberately vague about that, and a screen that reported "no such account" would
   * turn the reset form into a way of testing which emails exist.
   */
  if (sent) {
    return (
      <AuthShell
        title={t('com_ui_leadhr_forgot_sent_title')}
        intro={t('com_ui_leadhr_forgot_sent_intro', { email })}
        onSubmit={(event) => {
          event.preventDefault();
          onBack();
        }}
      >
        <AuthNotice tone="success">{t('com_ui_leadhr_forgot_sent_note')}</AuthNotice>
        <AuthSubmit
          busy={false}
          label={t('com_ui_leadhr_forgot_back')}
          busyLabel={t('com_ui_leadhr_forgot_back')}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('com_ui_leadhr_forgot_title')}
      intro={t('com_ui_leadhr_forgot_intro')}
      onSubmit={submit}
    >
      <AuthField
        id="leadhr-forgot-email"
        label={t('com_ui_leadhr_login_email')}
        type="email"
        autoFocus
        autoComplete="username"
        value={email}
        onChange={setEmail}
        placeholder={t('com_ui_leadhr_login_email_placeholder')}
        invalid={!!error}
        describedBy={error ? 'leadhr-forgot-error' : undefined}
      />
      {error ? (
        <AuthNotice id="leadhr-forgot-error" tone="error">
          {error}
        </AuthNotice>
      ) : null}
      <AuthSubmit
        busy={busy}
        label={t('com_ui_leadhr_forgot_submit')}
        busyLabel={t('com_ui_leadhr_forgot_busy')}
      />
      <AuthLink onClick={onBack}>{t('com_ui_leadhr_forgot_back')}</AuthLink>
    </AuthShell>
  );
}
